/**
 * Teslyn Cloud's addresses that are not a company's own (docs/specs/cloud/front-door.md): the front door
 * (`cloud.teslyn.au`, decision 699) and the forward (`cloud.teslyn.com.au` → `cloud.teslyn.au`, decision 681). Every
 * address this module makes comes from the build's settings; the address bar gives only the path and query, and a
 * path such as `//other.example` stays a path on the settings' host.
 */
import { cookieDomainFor, underDomain } from '../auth/sharedSession.ts';
import type { WebConfig } from '../config.ts';
import { isPreviewHost } from '../tenant/resolve.ts';

export interface Where {
  readonly hostname: string;
  readonly pathname: string;
  readonly search: string;
  readonly hash?: string;
}

const HOST = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

const norm = (h: string): string => h.toLowerCase().replace(/\.$/, '');

/** `?frontdoor` shows the front door on a preview host; `?choose` forgets the remembered company. */
export const FRONT_DOOR_PARAM = 'frontdoor';
export const CHOOSE_PARAM = 'choose';

/** What a sign-in return leaves in the query (auth/authReturn.ts removes them first; dropped again here). */
const RETURN_PARAMS = ['code', 'sb_flow_id', 'token_hash', 'error', 'error_code', 'error_description'];

/** The address this one sends the browser to (decision 681), with the same path, query and fragment; or null. */
export function forwardTarget(
  loc: Where,
  forwardHosts: ReadonlyMap<string, string> | undefined,
): string | null {
  const host = norm(loc.hostname);
  const to = forwardHosts?.get(host);
  if (!to || !HOST.test(to) || to === host) return null;
  const u = new URL(`https://${to}`);
  u.pathname = loc.pathname || '/';
  u.search = loc.search;
  u.hash = loc.hash ?? '';
  return u.toString();
}

/** Whether this address shows the front door: one of the build's front door hosts, or a preview with `?frontdoor`. */
export function isFrontDoor(
  loc: Pick<Where, 'hostname' | 'search'>,
  cfg: Pick<WebConfig, 'frontDoorHosts'>,
): boolean {
  const host = norm(loc.hostname);
  if ((cfg.frontDoorHosts ?? []).includes(host)) return true;
  return isPreviewHost(host) && new URLSearchParams(loc.search).has(FRONT_DOOR_PARAM);
}

/** A company's main address: the first host the build maps to its slug (list order), or null. */
export function companyHost(slug: string, customDomains: ReadonlyMap<string, string>): string | null {
  for (const [host, s] of customDomains) if (s === slug && HOST.test(host)) return host;
  return null;
}

export interface CompanyUrl {
  /** Where the browser goes: the company's address with the front door's path and query. */
  readonly url: string;
  /** The same site as the front door (a preview host): the sign-in is already there. */
  readonly sameSite: boolean;
  /**
   * The company's address shares the front door's sign-in (decision 978): the same site, or both `https` addresses
   * under the build's cookie domain (`cloud.teslyn.au` itself and `thomson.cloud.teslyn.au` under it). A company's
   * own domain outside it (`cloud.thomsonair.com.au`) does not.
   */
  readonly sharesSignIn: boolean;
}

/**
 * The company's address for this front door visit, keeping the page and query it was opened with (an alert link's
 * `?page=alerts&alert=…`, decision 741), or null when the build has no address for the company. A preview host shown
 * with `?frontdoor` stays on its own site; one listed in the build's front door hosts (a Pages branch alias made to
 * try the front door on staging) sends people to the companies' addresses like `cloud.teslyn.au`.
 */
export function companyUrl(
  slug: string,
  cfg: Pick<WebConfig, 'customDomains' | 'frontDoorHosts' | 'authCookieDomain'>,
  loc: Where & { readonly origin: string },
): CompanyUrl | null {
  const q = new URLSearchParams(loc.search);
  // `type` belongs to a return only beside a token hash
  if (q.has('token_hash')) q.delete('type');
  for (const k of [FRONT_DOOR_PARAM, CHOOSE_PARAM, ...RETURN_PARAMS]) q.delete(k);
  const host0 = norm(loc.hostname);
  if (isPreviewHost(host0) && !(cfg.frontDoorHosts ?? []).includes(host0)) {
    const u = new URL(loc.origin);
    u.pathname = loc.pathname || '/';
    q.set('tenant', slug);
    u.search = q.toString();
    return { url: u.toString(), sameSite: true, sharesSignIn: true };
  }
  const host = companyHost(slug, cfg.customDomains);
  if (!host) return null;
  q.delete('tenant');
  const u = new URL(`https://${host}`);
  u.pathname = loc.pathname || '/';
  u.search = q.toString();
  const domain = cookieDomainFor(host0, new URL(loc.origin).protocol, cfg.authCookieDomain);
  return { url: u.toString(), sameSite: false, sharesSignIn: underDomain(host, domain) };
}

const REMEMBER_KEY = 'teslyn.frontdoor.company';
const SLUG = /^[a-z0-9][a-z0-9-]{1,62}$/;

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function store(): Store | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The company this browser last went on to from the front door (its slug only), or null. */
export function rememberedCompany(s: Store | null = store()): string | null {
  try {
    const v = s?.getItem(REMEMBER_KEY) ?? null;
    return v && SLUG.test(v) ? v : null;
  } catch {
    return null;
  }
}

export function rememberCompany(slug: string | null, s: Store | null = store()): void {
  try {
    if (slug) s?.setItem(REMEMBER_KEY, slug);
    else s?.removeItem(REMEMBER_KEY);
  } catch {
    /* storage unavailable: the front door asks again next time */
  }
}
